function toLocalDateKey(input) {
    if (!input) return null;
    if (typeof input === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(input)) {
        return input;
    }
    const d = input instanceof Date ? input : new Date(input);
    if (Number.isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function addReadingDays(date, count, scheduleType = 'daily') {
    const result = new Date(date);
    let remaining = count;
    while (remaining > 0) {
        result.setDate(result.getDate() + 1);
        if (scheduleType === 'weekdays') {
            const dow = result.getDay();
            if (dow === 0 || dow === 6) continue;
        }
        remaining -= 1;
    }
    return result;
}

function countScheduledDays(startDate, endDate, scheduleType = 'daily') {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
    if (end < start) return 0;

    if (scheduleType !== 'weekdays') {
        const diffTime = end - start;
        return Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
    }

    let count = 0;
    const cursor = new Date(start);
    while (cursor <= end) {
        const dow = cursor.getDay();
        if (dow !== 0 && dow !== 6) count += 1;
        cursor.setDate(cursor.getDate() + 1);
    }
    return count;
}

export function getReadingDate(startDate, dayNumber, scheduleType = 'daily') {
    if (!startDate) return null;
    const start = new Date(startDate);
    if (Number.isNaN(start.getTime())) return null;

    let target = new Date(start);
    if (scheduleType !== 'weekdays') {
        const offset = dayNumber - 1;
        target.setDate(target.getDate() + offset);
        return toLocalDateKey(target);
    }

    let remaining = dayNumber;
    while (remaining > 0) {
        const dow = target.getDay();
        if (dow !== 0 && dow !== 6) {
            remaining -= 1;
            if (remaining === 0) break;
        }
        target.setDate(target.getDate() + 1);
    }
    return toLocalDateKey(target);
}

export function calculateStreaks(datesArray) {
    if (!datesArray || datesArray.length === 0) {
        return { currentStreak: 0, longestStreak: 0 };
    }

    // Sort unique dates (YYYY-MM-DD)
    const uniqueDates = [...new Set(
        datesArray
            .map(d => toLocalDateKey(d))
            .filter(Boolean)
    )].sort();

    if (uniqueDates.length === 0) return { currentStreak: 0, longestStreak: 0 };

    let currentStreak = 0;
    let longestStreak = 0;
    let tempStreak = 1;

    // Iterate
    for (let i = 1; i < uniqueDates.length; i++) {
        const prev = new Date(`${uniqueDates[i - 1]}T00:00:00`);
        const curr = new Date(`${uniqueDates[i]}T00:00:00`);

        const diffTime = Math.abs(curr - prev);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 1) {
            tempStreak++;
        } else {
            if (tempStreak > longestStreak) longestStreak = tempStreak;
            tempStreak = 1;
        }
    }
    if (tempStreak > longestStreak) longestStreak = tempStreak;

    // Check if current streak is active (ends today or yesterday)
    // const lastDate = new Date(...) - Unused
    const today = new Date();
    const todayStr = toLocalDateKey(today);
    const yest = new Date(today);
    yest.setDate(yest.getDate() - 1);
    const yestStr = toLocalDateKey(yest);
    const lastDateStr = uniqueDates[uniqueDates.length - 1];

    if (lastDateStr === todayStr || lastDateStr === yestStr) {
        currentStreak = tempStreak;
    } else {
        currentStreak = 0;
    }

    return { currentStreak, longestStreak };
}

export function calculatePlanStats(plan, progress) {
    if (!progress) return null;

    const completedDays = progress.completedDays || [];
    const completedCount = completedDays.length;
    const totalDays = plan.days ? plan.days.length : (plan.totalChapters || 365); // Fallback
    const scheduleType = plan.definition?.schedule?.type || 'daily';

    // Percentage
    const percentage = totalDays > 0 ? Math.round((completedCount / totalDays) * 100) : 0;

    // Ahead/Behind
    let status = 'Not Started';
    let daysDiff = 0;
    let expectedDay = 0;

    if (progress.startDate) {
        const now = new Date();
        const daysElapsed = countScheduledDays(progress.startDate, now, scheduleType);
        expectedDay = daysElapsed;

        if (completedCount === 0) {
            status = 'Not Started';
        } else {
            daysDiff = completedCount - expectedDay;
            if (daysDiff > 0) status = `${daysDiff} days ahead`;
            else if (daysDiff < 0) status = `${Math.abs(daysDiff)} days behind`;
            else status = 'On Track';
        }
    }

    // Streaks
    // If we have explicit completedDates map, use it.
    // If not, use migration logic (derived dates) OR just 0.
    // Requirement said: "For streaks, store completion date by real date... Use calendar date for the “reading day” derived..."
    // I will assume the store will handle populating `completedDates`.

    let datesList = progress.completedDates ? Object.values(progress.completedDates) : [];
    if (datesList.length === 0 && progress.startDate && completedDays.length > 0) {
        datesList = completedDays
            .map(dayNum => getReadingDate(progress.startDate, dayNum, scheduleType))
            .filter(Boolean);
    }
    const { currentStreak, longestStreak } = calculateStreaks(datesList);



    // Consistency Stats
    const consistency = calculateConsistency(datesList);

    return {
        completedCount,
        totalDays,
        percentage,
        status,
        daysDiff,
        expectedDay,
        currentStreak,
        longestStreak,
        lastCompletedAt: progress.lastCompletedAt || progress.lastUpdated,
        consistency // New field
    };
}

export function calculateConsistency(datesList) {
    if (!datesList || datesList.length === 0) {
        return {
            readsPerWeek: 0,
            busiestDay: 'N/A',
            mostConsistentMonth: 'N/A'
        };
    }

    const dates = datesList
        .map(d => {
            const key = toLocalDateKey(d);
            return key ? new Date(`${key}T00:00:00`) : null;
        })
        .filter(Boolean);
    const sortedDates = dates.sort((a, b) => a - b);
    const firstDate = sortedDates[0];
    const lastDate = new Date(); // Use now to properly gauge consistency over time elapsed

    // Reads Per Week
    const daysElapsed = Math.max(1, Math.ceil((lastDate - firstDate) / (1000 * 60 * 60 * 24)));
    const weeksElapsed = daysElapsed / 7;
    const readsPerWeek = Math.round((datesList.length / Math.max(1, weeksElapsed)) * 10) / 10;

    // Busiest Day
    const dayCounts = {};
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    // Most Consistent Month
    const monthCounts = {};
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    dates.forEach(d => {
        // Day of Week
        const dayName = daysOfWeek[d.getDay()];
        dayCounts[dayName] = (dayCounts[dayName] || 0) + 1;

        // Month (YYYY-MM to distinguish years)
        const monthKey = `${d.getFullYear()}-${d.getMonth()}`;
        monthCounts[monthKey] = (monthCounts[monthKey] || 0) + 1;
    });

    let busiestDay = 'N/A';
    let maxDayCount = 0;
    for (const [day, count] of Object.entries(dayCounts)) {
        if (count > maxDayCount) {
            maxDayCount = count;
            busiestDay = day;
        }
    }

    let consistentMonth = 'N/A';
    let maxMonthCount = 0;
    for (const [mKey, count] of Object.entries(monthCounts)) {
        if (count > maxMonthCount) {
            maxMonthCount = count;
            const [year, monthIdx] = mKey.split('-');
            consistentMonth = `${monthNames[parseInt(monthIdx)]} ${year}`;
        }
    }

    return {
        readsPerWeek,
        busiestDay,
        mostConsistentMonth: consistentMonth
    };
}

export function calculateCategoryStats(completedReadingsFlat, bibleData) {
    // completedReadingsFlat: Array of readings { book: "Genesis", chapter: 1 } or similar from ALL completed days.
    // bibleData: The full JSON structure.

    if (!bibleData || !completedReadingsFlat || completedReadingsFlat.length === 0) return [];

    const categoryStats = {};
    // Init Categories based on Genre/Groups seen in bibleData
    // We can aggregate by "genre" array in book def.
    // Common genres: Law, History, Poetry, Wisdom (sometimes same), Major Prophets, Minor Prophets, Gospels, History (NT), Pauline Epistles, General Epistles, Prophecy/Revelation.

    // We'll trust the 'genre' field. 
    // Or we custom map for "high level categorization user requested" (Pauline Epistles mentions).
    // The JSON I saw had "Law", "History", "Poetry".

    completedReadingsFlat.forEach(reading => {
        const bookDef = bibleData.books.find(b => b.name === reading.book);
        if (!bookDef) return;

        // Use first genre or specific logic
        let category = bookDef.genre ? bookDef.genre[0] : 'Unknown';

        // Refine categories not in JSON explicitly if needed (e.g. Epistles)
        // I need to see full JSON to know if "Pauline Epistles" is a genre there.
        // Assuming "Law", "History", "Poetry", "Prophecy", "Gospels", "Letters/Epistles".

        // If JSON just says "Epistles", I might need to sub-categorize if user wants "Pauline".
        // But for MVP, using JSON genres is safest.

        if (!categoryStats[category]) {
            categoryStats[category] = { read: 0, total: 0, name: category };
        }

        // We are counting Chapters read? Or Verses? User said "percentage read of ...".
        // Usually means chapters or books. Chapters is granular.
        categoryStats[category].read += 1;
    });

    // Calculate Totals for relevant categories (So we know percentage of THAT category)
    // We need to iterate ALL Bible books to sum totals for these categories.
    // Optimization: Check existing keys in categoryStats.

    const bibleTotals = {};
    bibleData.books.forEach(b => {
        let cat = b.genre ? b.genre[0] : 'Unknown';
        if (!bibleTotals[cat]) bibleTotals[cat] = 0;
        bibleTotals[cat] += b.chapters;
    });

    // Merge totals
    const result = Object.values(categoryStats).map(stat => {
        stat.total = bibleTotals[stat.name] || 0;
        stat.percentage = stat.total > 0 ? Math.round((stat.read / stat.total) * 100) : 0;
        return stat;
    });

    return result.sort((a, b) => b.percentage - a.percentage);
}

export function calculateProjectedFinish(plan, progress) {
    // If averageDailyRate (chapters/day) is provided, use it.
    // Else use plan pace calculation.

    // Simpler: Days Remaining / (CompletedDays / DaysElapsed)
    if (!progress || progress.completedDays.length === 0) return 'N/A';

    const totalItems = plan.days ? plan.days.length : plan.durationDays;
    const remaining = totalItems - progress.completedDays.length;

    if (remaining <= 0) return 'Done';

    const scheduleType = plan.definition?.schedule?.type || 'daily';
    const startDate = progress.startDate ? new Date(progress.startDate) : new Date();
    const now = new Date();
    const daysElapsed = Math.max(1, countScheduledDays(startDate, now, scheduleType));

    const dailyRate = progress.completedDays.length / daysElapsed;

    if (dailyRate <= 0) return 'Unknown';

    const daysLeft = Math.ceil(remaining / dailyRate);
    const finishDate = addReadingDays(now, daysLeft, scheduleType);

    return finishDate.toLocaleDateString();
}
